import React, { FunctionComponent, useEffect, useLayoutEffect, useReducer } from 'react';
import { Button, Card, Pill, Text, Toast, isMobileDevice } from 'rs-emd-ui-atoms';
import { getLabel } from '../../../..';
import { useUserDispatch } from '../../../../../../components/app/user-context';
import { clickEvent } from '../../../../../../tagging/ensighten';
import { MetaDataAndTagging } from '../../../shared';
import { Action, LoginPageActionType, LoginPageProps, State } from './login-page-component.model';
import styles from './login-page-component.module.scss';

export const LoginPage: FunctionComponent<LoginPageProps> = (props: LoginPageProps) => {
  const userDispatch = useUserDispatch();
  const [state, dispatch] = useReducer(reducer, {
    username: props.identifier ?? '',
    password: '',
    userSignInAttempt: false,
    isPasswordVisible: false,
    isLogInBtnClicked: false,
    isClosableToastVisible: false,
  });

  //TODO: check if working - needs tagging
  //push event each time login page is accesses
  useLayoutEffect(() => {
    props.loginService.pushEvent();
  }, [props.loginService]);

  //to show open transition
  useEffect(() => {
    if (
      props.redirectFromForgotPassword ||
      props.redirectFromSuccessfulRegister ||
      props.redirectFromResetPassword ||
      props.redirectFromLogout
    ) {
      dispatch({ type: LoginPageActionType.ToggleClosableToast, isVisible: true });
    }
  }, [props.redirectFromForgotPassword, props.redirectFromLogout, props.redirectFromResetPassword, props.redirectFromSuccessfulRegister]);

  return (
    <div className={`inner-page-wrapper ${styles['login-page-container']}`}>
      {/* Head data */}
      <MetaDataAndTagging tagging={props.data.tagging} />

      <p className={styles.title}>{getLabel(props.data.labels, 'login_title')}</p>
      <Card className={styles.card} radius={0}>
        <form className={styles['login-form-container']} onSubmit={loginUser}>
          {/**TODO: add email verification */}
          <div className={styles['user-input']}>
            <Text
              autoComplete='on'
              name='username'
              autoFocus={!isMobileDevice()}
              tabIndex={2}
              placeholder={getLabel(props.data.labels, 'username')}
              className={styles.text}
              onChange={(e) =>
                dispatch({
                  type: LoginPageActionType.SetCredentials,
                  credentialType: 'username',
                  credential: e.currentTarget.value,
                })
              }
              value={state.username}
              errorMessage={getLabel(props.data.labels, 'please_enter_username')}
              isValid={!state.isUsernameEmpty}
              onKeyDown={onInputFieldKeyDown}
            />
          </div>
          <div className={`${styles['user-input']} ${styles.password}`}>
            <Text
              autoComplete='on'
              name='password'
              tabIndex={3}
              className={styles.text}
              placeholder={getLabel(props.data.labels, 'password')}
              type={state.isPasswordVisible ? 'text' : 'password'}
              icon={state.isPasswordVisible ? 'hide' : 'show'}
              alignIcon='right'
              onClickIcon={() => dispatch({ type: LoginPageActionType.TogglePasswordVisibility })}
              onChange={(e) =>
                dispatch({
                  type: LoginPageActionType.SetCredentials,
                  credentialType: 'password',
                  credential: e.currentTarget.value,
                })
              }
              errorMessage={getLabel(props.data.labels, 'please_enter_password')}
              isValid={!state.isPasswordEmpty}
              onKeyDown={onInputFieldKeyDown}
            />
            <a
              tabIndex={7}
              className={styles.forgot}
              href={props.isProfileEnabled ? '/user/forgotPassword' : '/ForgotPassword/ForgotPassword'}
              onClick={onResetPasswordClick}>
              {getLabel(props.data.labels, 'forgot_password')}
            </a>
          </div>
          {state.userSignInAttempt && !state.emptyUserCredentials && !state.isUserLoggedIn && (
            <Pill class={styles['no-user-pill']}>
              <span dangerouslySetInnerHTML={{ __html: getLabel(props.data.labels, 'login_error') }} />
            </Pill>
          )}
          <Button
            tabIndex={4}
            disabled={state.isLogInBtnClicked}
            icon={state.isLogInBtnClicked ? 'circular-loader' : undefined}
            text={getLabel(props.data.labels, 'log_in')}
            size='full-width'
            type='submit'
          />
          <div className={styles.register}>
            <span className={styles.instruction}>{getLabel(props.data.labels, 'do_not_have_account')}</span>
            <a tabIndex={5} href='/user/register'>
              {getLabel(props.data.labels, 'register')}
            </a>
          </div>
        </form>
      </Card>

      {(props.redirectFromForgotPassword ||
        props.redirectFromSuccessfulRegister ||
        props.redirectFromResetPassword ||
        props.redirectFromLogout) && (
        <Toast
          showToast={state.isClosableToastVisible}
          backgroundColour='success'
          icon='check'
          text={getLabel(
            props.data.labels,
            props.redirectFromForgotPassword
              ? 'forgot_password_toast'
              : props.redirectFromResetPassword
              ? 'reset_password_toast'
              : props.redirectFromLogout
              ? 'logout_toast'
              : 'success_registration_toast'
          )}
          onCloseCallback={() => dispatch({ type: LoginPageActionType.ToggleClosableToast, isVisible: false })}
        />
      )}
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case LoginPageActionType.SetCredentials:
        return {
          ...state,
          username: action.credentialType === 'username' ? action.credential : state.username,
          password: action.credentialType === 'password' ? action.credential : state.password,
        };
      case LoginPageActionType.SignInAttempt:
        return {
          ...state,
          userSignInAttempt: true,
          isUserLoggedIn: undefined,
          emptyUserCredentials: false,
          isLogInBtnClicked: false,
        };
      case LoginPageActionType.SignInUser:
        return {
          ...state,
          isUserLoggedIn: action.isSuccess,
          emptyUserCredentials: false,
          isPasswordEmpty: false,
          isUsernameEmpty: false,
        };
      case LoginPageActionType.TogglePasswordVisibility:
        return {
          ...state,
          isPasswordVisible: !state.isPasswordVisible,
        };
      case LoginPageActionType.UserEmptyCredential:
        return {
          ...state,
          userSignInAttempt: true,
          emptyUserCredentials: true,
          isUsernameEmpty: action.isUsernameEmpty,
          isPasswordEmpty: action.isPasswordEmpty,
          isLogInBtnClicked: false,
        };
      case LoginPageActionType.SetIsLogInBtnClicked:
        return {
          ...state,
          isLogInBtnClicked: true,
        };
      case LoginPageActionType.ToggleClosableToast:
        return {
          ...state,
          isClosableToastVisible: action.isVisible,
        };
    }
  }

  function loginUser(e: any) {
    // to prevent URL change
    e.preventDefault();

    dispatch({ type: LoginPageActionType.SetIsLogInBtnClicked });

    const isUsernameEmpty = state.username.length === 0;
    const isPasswordEmpty = state.password.length === 0;

    if (!isUsernameEmpty && !isPasswordEmpty) {
      props.loginService.login({ username: state.username, password: state.password }, userDispatch).then((res) => {
        if (res.data !== undefined) {
          dispatch({ type: LoginPageActionType.SignInUser, isSuccess: res.data.isSuccess });

          if (res.data.isSuccess) {
            if (res.data?.redirectUrl) {
              window.location.href = res.data.redirectUrl;
            } else {
              window.location.href = props.redirectionURL ?? '/';
            }
          } else {
            dispatch({ type: LoginPageActionType.SignInAttempt });
          }
        }
      });
    } else {
      dispatch({ type: LoginPageActionType.UserEmptyCredential, isUsernameEmpty: isUsernameEmpty, isPasswordEmpty: isPasswordEmpty });
    }
  }

  function onInputFieldKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') loginUser(e);
  }

  function onResetPasswordClick() {
    clickEvent({ __Info: 'Reset Password Guest', __Result: 'Guest' });
  }
};

export default LoginPage;
